import { attachmentContract } from "@/api/dilivery/sales/contract";
import { attachmentDownload } from "@/api/dilivery/audit";
import { getDataInLocalStorage } from "@/utils/localStorage";
import projectAnnexMixin from "@/mixins/projectAnnexMixin";

const baseURL = process.env.VUE_APP_BASE_API;

/**
 * 合同变更公共混入 - ContractChangeDetail / ChangeContractAudit 共用
 * 提供：变更数据定义、数据监听、字段映射、下载/预览、工具方法
 */
export default {
  mixins: [projectAnnexMixin],
  data() {
    return {
      showFile: false,
      showContractInfo: true,
      fileInfo: '',
      oldData: {},
      newData: {},
      changeData: [
        { path: 'contractNumber', name: '合同编号', oldValue: '', newValue: '' },
        { path: 'contractName', name: '合同名称', oldValue: '', newValue: '' },
        { path: 'sales.name', name: '销售', oldValue: '', newValue: '' },
        { path: 'expirationDate', name: '有效期限', oldValue: '', newValue: '' },
        { path: 'contractDraftingDate', name: '合同拟定时间', oldValue: '', newValue: '' },
        { path: 'contractAmount', name: '合同金额', oldValue: '', newValue: '' },
        { path: 'addedAmount', name: '增值金额', oldValue: '', newValue: '' },
        { path: 'generateContractInfo.returnSampleBo.returnSampleFlag', name: '是否返样', oldValue: '', newValue: '' },
        { path: 'generateContractInfo.returnSampleBo.returnSampleAmount', name: '服务费(返样)', oldValue: '', newValue: '' },
        { path: 'generateContractInfo.returnSampleBo.reason', name: '服务费(返样)为0原因', oldValue: '', newValue: '' },
        { path: 'customerContacts.contactName', name: '客户联系人', oldValue: '', newValue: '' },
        { path: 'baseCompany.companyName', name: '受托方', oldValue: '', newValue: '' },
        { path: 'trusteeContacts.name', name: '受托方联系人', oldValue: '', newValue: '' },
        { path: 'contractType', name: '合同分类', oldValue: '', newValue: '' },
        { path: 'projectType', name: '合同类型', oldValue: '', newValue: '' },
        { path: 'sampleManagement', name: '样品处理方式', oldValue: '', newValue: '' },
        { path: 'generateContractInfo.extractionFlag', name: '核酸抽提', oldValue: '', newValue: '' },
        { path: 'createTime', name: '创建时间', oldValue: '', newValue: '' },
        { path: 'signingPlace', name: '签订地点', oldValue: '', newValue: '' },
        { path: 'priceFilePath', name: '价格申请截图', oldValue: '', newValue: '' },
        { path: 'contractFilePath', name: '合同', oldValue: '', newValue: '' },
        { path: 'projectAnnex', name: '需做的理化性质列表', oldValue: '', newValue: '' },
        { path: 'remark', name: '备注', oldValue: '', newValue: '' },
      ],
      analysis: [
        { label: 'SENIOR_ANALYSIS', name: '高级分析' },
        { label: 'STANDARD_ANALYSIS', name: '标准分析' },
        { label: 'CUSTOMIZED_ANALYSIS', name: '个性化分析' },
      ],
    };
  },
  watch: {
    selectDetail: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.newData = JSON.parse(newVal['dataChange']);
          this.newData.id = newVal.id;
          this.oldData = newVal['rawData'] ? JSON.parse(newVal['rawData']) : { ...newVal.data };
          this.newData.seniorAnalysisAnnex = newVal['annex']['SENIOR_ANALYSIS_ANNEX'];
          this.newData.customizedAnalysisAnnex = newVal['annex']['CUSTOMIZED_ANALYSIS_ANNEX'];
          this.newData.contractFilePath = newVal['annex']['CONTRACT_ANNEX'];
          this.newData.priceFilePath = newVal['annex']['PRICE_APPLE_ANNEX'];
        }
      },
      immediate: true
    },
    newData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal)) {
          for (const data of this.changeData) {
            this._mapField(data, 'newValue', newVal);
          }
        }
      },
      immediate: true
    },
    oldData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal)) {
          for (const data of this.changeData) {
            this._mapField(data, 'oldValue', newVal);
          }
        }
      },
      immediate: true
    },
  },
  methods: {
    // ========== 字段值映射 ==========
    /**
     * 将原始数据按 path 映射为显示文本，写入 dataItem 的 valueKey 属性
     * @param {Object} dataItem  changeData 中的条目
     * @param {String} valueKey  'newValue' 或 'oldValue'
     * @param {Object} rawData   原始数据对象
     */
    _mapField(dataItem, valueKey, rawData) {
      const rawValue = this.hashTime(dataItem.path, rawData);

      // null 值处理：文件路径类型设为空串（用于显示下载/预览按钮区域），其他显示提示文本
      if (rawValue === null) {
        if (['priceFilePath', 'contractFilePath', 'projectAnnex'].includes(dataItem.path)) {
          dataItem[valueKey] = '';
        } else {
          dataItem[valueKey] = '系统中不存在' + dataItem.name + '数据';
        }
        return;
      }

      // 按字段路径做特殊映射
      switch (dataItem.path) {
        case 'expirationDate':
          dataItem[valueKey] = rawValue + '年';
          break;
        case 'sampleManagement':
          dataItem[valueKey] = rawValue === 'RETURN_SAMPLE' ? '返样' : rawValue === 'SAMPLE_HOLD' ? '暂存样品' : rawValue;
          break;
        case 'contractType': {
          const typeMap = {
            PREPAYMENTS: '预付款', FORMAL: '正式', CHARGEBACKS: '扣款',
            IMPORT_PREPAYMENTS: '导入预付款', DEVELOPMENT: '研发', TEAM: '团队费用'
          };
          dataItem[valueKey] = typeMap[rawValue] || rawValue;
          break;
        }
        case 'addedAmount':
          dataItem[valueKey] = ['PREPAYMENTS', 'IMPORT_PREPAYMENTS'].includes(rawData.contractType)
            ? rawValue : 'NA';
          break;
        case 'generateContractInfo.extractionFlag':
        case 'generateContractInfo.returnSampleBo.returnSampleFlag':
          dataItem[valueKey] = rawValue ? '是' : '否';
          break;
        case 'generateContractInfo.returnSampleBo.returnSampleAmount':
          dataItem[valueKey] = (!rawValue && Number(rawValue) !== 0) ? 'NA' : rawValue;
          break;
        case 'generateContractInfo.returnSampleBo.reason':
          dataItem[valueKey] = !rawValue ? 'NA' : rawValue;
          break;
        case 'projectAnnex':
          dataItem[valueKey] = this.findProjectAnnexProject(rawData) || '';
          break;
        default:
          dataItem[valueKey] = rawValue;
      }
    },

    // ========== 判断方法 ==========
    isAnalysisProject(data) {
      return ['预付款扣款（测序分析）', '测序分析'].includes(data.projectType) && data.contractType !== 'DEVELOPMENT';
    },
    hasMoreAnalysisRequest(data) {
      return Object.keys(data.moreAnalysisRequest).length > 0;
    },
    hasProtectiveFluidInfo(data) {
      return data && data.protectiveFluidInfo && data.protectiveFluidInfo.protectiveFluidMethod;
    },
    hasPendingProtectiveFluidMail(data) {
      return data && Array.isArray(data.protectiveFluidMail) && data.protectiveFluidMail.some(item => item.trackingFlag === 0);
    },
    hasProjectAnnex(data) {
      return data && Array.isArray(data.specificProjectType) &&
        data.specificProjectType.some(p => p.annexFilePath && p.projectMainTypeId);
    },

    // ========== 数据工具 ==========
    hashTime(newKey, data) {
      try {
        if (newKey.includes('.')) {
          const keys = newKey.split('.');
          return keys.reduce((obj, key) => obj && obj[key], data);
        }
        return data[newKey];
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getData(info) {
      return this[info];
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === '') return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return '¥' + num.toLocaleString('zh-CN');
    },
    compareHardDisks(oldList = [], newList = []) {
      const map = new Map();
      (oldList || []).forEach(d => {
        const key = d?.hardDiskSize ?? JSON.stringify(d);
        map.set(key, { hardDiskSize: d?.hardDiskSize, old: { projectAmount: d?.projectAmount ?? null, sampleCount: d?.sampleCount ?? null }, new: { projectAmount: null, sampleCount: null } });
      });
      (newList || []).forEach(d => {
        const key = d?.hardDiskSize ?? JSON.stringify(d);
        if (map.has(key)) {
          const obj = map.get(key);
          obj.new = { projectAmount: d?.projectAmount ?? null, sampleCount: d?.sampleCount ?? null };
          map.set(key, obj);
        } else {
          map.set(key, { hardDiskSize: d?.hardDiskSize, old: { projectAmount: null, sampleCount: null }, new: { projectAmount: d?.projectAmount ?? null, sampleCount: d?.sampleCount ?? null } });
        }
      });
      return Array.from(map.values());
    },

    // ========== 文件操作 ==========
    async download(id, type, status) {
      try {
        const annexMap = {
          contract: 'CONTRACT_ANNEX',
          senior: 'SENIOR_ANALYSIS_ANNEX',
          customized: 'CUSTOMIZED_ANALYSIS_ANNEX',
          price: 'PRICE_APPLE_ANNEX'
        };
        const annex = annexMap[type];
        const downloadURL = status === 'oldData'
          ? '/sequencing-contract/attachment/'
          : '/audit-info/attachment/';
        const url = `${baseURL}${downloadURL}${id}/${annex}?Authorization=${getDataInLocalStorage("token")}&Current-Role=${getDataInLocalStorage("role")}`;
        const elink = document.createElement('a');
        elink.href = url;
        document.body.appendChild(elink);
        elink.click();
        document.body.removeChild(elink);
      } catch (error) {
        console.log(error);
      }
    },
    async preview(id, type, status) {
      try {
        const annexMap = {
          contract: 'CONTRACT_ANNEX',
          senior: 'SENIOR_ANALYSIS_ANNEX',
          customized: 'CUSTOMIZED_ANALYSIS_ANNEX',
          price: 'PRICE_APPLE_ANNEX'
        };
        const annex = annexMap[type];
        if (status === 'oldData') {
          this.fileInfo = await attachmentContract(id, annex);
        } else {
          this.fileInfo = await attachmentDownload(id, annex);
        }
        this.showFile = true;

      } catch (error) {
        console.log(error);
      }
    },
    close() {
      this.showFile = false;
    },
    cancel() {
      this.$emit('submit', 'cancel');
    },
  }
};
